import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Store } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { BlocoOferta, rotuloVariedade } from "@/components/mercado/bloco-oferta";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { usePracas } from "@/hooks/use-cotacoes";
import { cn } from "@/lib/utils";
import type { OfertaDaPracaDTO } from "@shared/mercado-ofertas";
import { usePracaComprador, useResponderNaPraca } from "@/hooks/use-ofertas";
import { DialogMinhasPracas } from "@/components/mercado/dialog-minhas-pracas";

/**
 * PRAÇA — as cargas que produtores da praça deste comprador colocaram à venda.
 *
 * ⚠️ Tudo aqui é ANÔNIMO até o produtor abrir os detalhes: o card mostra kg,
 * classificação estimada e o preço da praça, e não diz de quem é. É o que
 * permite um produtor com quem ele nunca negociou alcançá-lo — que é o ponto do
 * ciclo.
 *
 * Mobile-first: o comprador abre isto no celular, entre uma carga e outra.
 */
export default function CompradorPraca() {
  const { user } = useAuth();
  const ativo = user?.orgTipo === "COMPRADOR";
  const [praca, setPraca] = useState<string>("");
  const [variedade, setVariedade] = useState<string>("");
  const [editandoPracas, setEditandoPracas] = useState(false);

  const consulta = usePracaComprador({ praca, variedade }, ativo);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "cards", linhas: 3 });

  return (
    <CompradorShell className="max-w-3xl mx-auto space-y-4">
      <PageHeader
        titulo="Praça"
        descricao="Cargas que produtores da sua praça colocaram à venda, ao preço da praça."
        empilharQuandoEstreito
        acao={
          <Button variant="outline" onClick={() => setEditandoPracas(true)} data-testid="button-minhas-pracas">
            <Store className="h-4 w-4 mr-1.5" />Minhas praças
          </Button>
        }
      />

      {aguardando || !data ? (
        aguardando
      ) : (
        <>
          {data.derivadaDoVinculo && data.pracas.length > 0 && (
            <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-3 py-2" data-testid="aviso-praca-derivada">
              Mostrando as praças dos produtores que já vendem para você. Escolha as suas em <b>Minhas praças</b>.
            </p>
          )}

          {data.pracas.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="filtro-pracas">
              <ChipFiltro ativo={praca === ""} onClick={() => setPraca("")}>Todas</ChipFiltro>
              {data.pracas.map(p => (
                <ChipFiltro key={p.slug} ativo={praca === p.slug} onClick={() => setPraca(p.slug)}>
                  {p.nome}
                </ChipFiltro>
              ))}
            </div>
          )}

          <div className="flex items-center gap-1.5 flex-wrap" data-testid="filtro-variedade">
            <ChipFiltro ativo={variedade === ""} onClick={() => setVariedade("")}>Tudo</ChipFiltro>
            <ChipFiltro ativo={variedade === "PRATA"} onClick={() => setVariedade("PRATA")}>Prata</ChipFiltro>
            <ChipFiltro ativo={variedade === "NANICA"} onClick={() => setVariedade("NANICA")}>Nanica</ChipFiltro>
          </div>

          {data.pracas.length === 0 ? (
            <EmptyState
              icone={Store}
              titulo="Escolha sua praça"
              descricao="Diga em quais praças você compra e as cargas ofertadas por lá aparecem aqui."
              acao={<Button onClick={() => setEditandoPracas(true)}>Minhas praças</Button>}
            />
          ) : data.ofertas.length === 0 ? (
            <EmptyState
              icone={Store}
              titulo="Nenhuma carga na praça agora"
              descricao="Quando um produtor da sua praça ofertar uma carga, ela aparece aqui — com kg, classificação estimada e o preço do dia."
            />
          ) : (
            <div className="space-y-3">
              {data.ofertas.map(oferta => <CardOfertaPraca key={oferta.id} oferta={oferta} />)}
            </div>
          )}
        </>
      )}

      {editandoPracas && (
        <DialogMinhasPracas
          atuais={data?.derivadaDoVinculo ? [] : (data?.pracas ?? []).map(p => p.slug)}
          onFechar={() => setEditandoPracas(false)}
        />
      )}
    </CompradorShell>
  );
}

function ChipFiltro({ ativo, onClick, children }: { ativo: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-8 px-3 rounded-full border text-xs font-medium transition-colors",
        ativo ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted",
      )}
    >
      {children}
    </button>
  );
}

/**
 * Um card da praça.
 *
 * ⚠️ A resposta é INTERESSE, e só (059): não há campo de kg nem "quero tudo".
 * Quanto, quando e por quanto se acerta entre os dois no WhatsApp, com o preço
 * da praça no dia da carga — e a tela só lista oferta NA PRAÇA, então não há
 * card fechado para rotular.
 */
function CardOfertaPraca({ oferta }: { oferta: OfertaDaPracaDTO }) {
  const { toast } = useToast();
  const responder = useResponderNaPraca();

  const minha = oferta.respondida;

  function enviar() {
    responder.mutate(oferta.id, {
      onSuccess: () => toast({ title: "Interesse registrado", description: "O produtor vai ver e pode abrir os detalhes para você." }),
      onError: (e: any) => toast({ title: "Não foi possível responder", description: e?.message, variant: "destructive" }),
    });
  }

  return (
    <section className="rounded-xl border bg-card p-4 space-y-3" data-testid={`card-praca-${oferta.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          {oferta.praca.nome} · {rotuloVariedade(oferta.variedade)}
        </p>
      </div>

      <BlocoOferta oferta={oferta} compacto />

      {oferta.produtor && (
        <div className="rounded-lg border bg-background p-3 space-y-1.5" data-testid={`produtor-${oferta.id}`}>
          <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Quem está ofertando</p>
          <p className="text-sm font-semibold">{oferta.produtor.nome}</p>
          <p className="text-xs text-muted-foreground">{oferta.produtor.fazenda}</p>
          {oferta.produtor.telefone && (
            <Button
              variant="outline"
              className="w-full min-h-11"
              onClick={() => window.open(`https://wa.me/55${oferta.produtor!.telefone.replace(/\D/g, "")}`, "_blank")}
              data-testid={`button-whatsapp-${oferta.id}`}
            >
              <MessageCircle className="h-4 w-4 mr-1.5" />Falar no WhatsApp
            </Button>
          )}
        </div>
      )}

      {minha ? (
        <p className="text-xs text-muted-foreground" data-testid={`texto-respondida-${oferta.id}`}>
          Você disse que tem interesse · aguardando o produtor
        </p>
      ) : (
        <Button
          className="w-full h-11"
          disabled={responder.isPending}
          onClick={enviar}
          data-testid={`button-tenho-interesse-${oferta.id}`}
        >
          Tenho interesse
        </Button>
      )}
    </section>
  );
}

/** "Minhas praças": onde este comprador compra. Só praças ATIVAS do cadastro. */
